import { Button, Card, CardBody, CardProps, Flex, Stack, useToast } from '@chakra-ui/react';
import { buildFlightPathString, copyText, Show } from '@pelicargo/ui';
import { useCallback } from 'react';
import { HiClipboardCopy } from 'react-icons/hi';
import { HiArrowPath } from 'react-icons/hi2';

import { TrpcOutputs } from '../config/trpc';
import { useCanChangeFlight } from '../hooks/useCanChangeFlight';
import { useFlightData } from '../hooks/useFlightData';
import { useSelectedQuoteVariant } from '../hooks/useSelectedQuoteVariant';
import { QuoteScheduleModal } from './QuoteScheduleModal';
import { Timeline } from './Timeline';

type Props = CardProps & {
  flightSegments: TrpcOutputs['getQuote']['flight_path'];
  quote: TrpcOutputs['getQuote'];
  estimatedTransitTime?: string;
  showHeader?: boolean;
  airlineName?: string;
  airlineLogoUri?: string;
  pickupOrigin?: string;
  originAirportId?: string;
  destinationAirportId?: string;
};

export const FlightSegmentsCard = ({
  flightSegments = [],
  quote,
  pickupOrigin,
  originAirportId,
  ...rest
}: Props) => {
  const toast = useToast();
  const timelineData = useFlightData(flightSegments, { pickupOrigin });

  const copySegments = useCallback(() => {
    const text = buildFlightPathString(flightSegments, { pickupOrigin, originAirportId });
    copyText(text);
    toast({ colorScheme: 'green', title: 'Copied!' });
  }, [flightSegments, originAirportId, pickupOrigin, toast]);


  const { modalDisclosure } = useSelectedQuoteVariant();
  const canChangeFlight = useCanChangeFlight(quote);

  return (
    <>
      <Card h="auto" {...rest} px="4" py="3" borderRadius="lg">
        <CardBody p="0" role="group">
          <Stack h="full">
            <Flex gap="2"
              justifyContent="flex-end"
            >
              <Button
                size="sm"
                variant="ghost"
                bg="bg.default"
                leftIcon={<HiClipboardCopy />}
                onClick={copySegments}
                opacity="0"
                _groupHover={{ opacity: 1 }}
              >
                Copy
              </Button>
              <Show if={canChangeFlight}>
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<HiArrowPath />}
                  bg="bg.subtle"
                  onClick={modalDisclosure.onOpen}
                  _hover={{ bg: 'gray.200' }}
                >
                  Change Flight
                </Button>
              </Show>
            </Flex>
            <Flex justifyContent="space-between">
              <Stack h="full" justify="space-between">
                <Stack direction="row" spacing="4" mt="2" justify="space-between">
                  <Timeline data={timelineData?.flightData} />
                </Stack>
              </Stack>
            </Flex>
          </Stack>
        </CardBody>
      </Card>
      <QuoteScheduleModal quote={quote} {...modalDisclosure} />
    </>
  );
};
